import * as yup from 'yup';
import { errorMessages } from '~/constants/errorMessages.ts';
import { FATCA_TIN, MAX_INPUT_LENGTH, REFUTATION_DOC_TYPE } from './constants';
import { regularExpressions } from '~/constants/regularExpressions';
import * as Yup from 'yup';
import { RNOKPPIdentNumberSchema } from '../../validationSchemas';

export const SelectItemYup = yup.object({
  value: yup.string().required(errorMessages.FIELD_REQUIRED),
  label: yup.string().required(errorMessages.FIELD_REQUIRED),
});

const endDateSchema = yup
  .string()
  .notRequired()
  .nullable()
  .test('end-after-start', errorMessages.PEP_END_AFTER_START_DATE, (date, context) => {
    if (!date || !context.parent?.publicFunctionsStartDate) return true;

    const [endDay, endMonth, endYear] = date.split('.');
    const [startDay, startMonth, startYear] = context.parent?.publicFunctionsStartDate.split('.');

    const publicFunctionsEndDate = new Date(`${endYear}-${endMonth}-${endDay}`);
    const publicFunctionsStartDate = new Date(`${startYear}-${startMonth}-${startDay}`);
    return publicFunctionsEndDate >= publicFunctionsStartDate;
  });

export const relatedPersonPep = yup.object().shape({
  fullName: yup
    .string()
    .required(errorMessages.FIELD_REQUIRED)
    .test('ukr-or-latin', errorMessages.LATIN_AND_UKR_CHARACTERS_ONLY, function (value) {
      if (!value) return true;

      const isUkrainian = regularExpressions.UKR_CHARACTERS_ONLY.test(value);
      const isLatin = regularExpressions.LATIN_CHARACTERS_ONLY.test(value);

      if (isUkrainian || isLatin) return true;

      return this.createError({
        path: this.path,
        message: errorMessages.LATIN_AND_UKR_CHARACTERS_ONLY,
      });
    })
    .max(MAX_INPUT_LENGTH, errorMessages.MAX_LENGTH(MAX_INPUT_LENGTH)),
  identificationNumber: RNOKPPIdentNumberSchema(),
  foreignTaxNumber: yup.string().nullable().notRequired(),
  citizenshipCountry: SelectItemYup.required(errorMessages.FIELD_REQUIRED),
  residenceCountry: SelectItemYup.required(errorMessages.FIELD_REQUIRED),
  pepPosition: yup
    .string()
    .required(errorMessages.FIELD_REQUIRED)
    .max(256, errorMessages.MAX_LENGTH(256)),
  publicFunctionsStartDate: yup.string().required(errorMessages.FIELD_REQUIRED),
  publicFunctionsEndDate: endDateSchema,
});

const clientPep = (schema: yup.ObjectSchema<{}, yup.AnyObject, {}, ''>) =>
  schema.shape({
    fullName: yup
      .string()
      .required(errorMessages.FIELD_REQUIRED)
      .test('ukr-or-latin', errorMessages.LATIN_AND_UKR_CHARACTERS_ONLY, function (value) {
        if (!value) return true;

        const isUkrainian = regularExpressions.UKR_CHARACTERS_ONLY.test(value);
        const isLatin = regularExpressions.LATIN_CHARACTERS_ONLY.test(value);

        if (isUkrainian || isLatin) return true;

        return this.createError({
          path: this.path,
          message: errorMessages.LATIN_AND_UKR_CHARACTERS_ONLY,
        });
      })
      .max(MAX_INPUT_LENGTH, errorMessages.MAX_LENGTH(MAX_INPUT_LENGTH)),
    pepPosition: yup
      .string()
      .required(errorMessages.FIELD_REQUIRED)
      .max(256, errorMessages.MAX_LENGTH(256)),
    publicFunctionsStartDate: yup.string().required(errorMessages.FIELD_REQUIRED),
    publicFunctionsEndDate: endDateSchema,
  });

export const jurisdictionsSchema = Yup.array().of(
  Yup.object().shape({
    country: Yup.object()
      .shape({
        value: Yup.string().nullable(),
        label: Yup.string().required(errorMessages.FIELD_REQUIRED),
      })
      .nullable()
      .required(errorMessages.FIELD_REQUIRED),
    identifyCode: yup.string().nullable().notRequired(),
    reason: Yup.mixed().when('identifyCode', {
      is: (value: string) => !!value,
      then: () => Yup.mixed().nullable().notRequired(),
      otherwise: () => Yup.string().required(errorMessages.ANSWER_OPTION),
    }),
    explanation: Yup.mixed().when('reason', {
      is: (type: string) => type === 'B' || type === 'C',
      then: () => Yup.string().required(errorMessages.FIELD_REQUIRED),
      otherwise: () => Yup.mixed().nullable().notRequired(),
    }),
  })
);

export const pepValidationSchema = (isCRSIFopEnabled: boolean, isSoleTrader: boolean) =>
  yup.array().of(
    yup
      .object()
      .shape({
        isPepsAbsent: yup.boolean().nullable(),
        isPep: yup.boolean().nullable(),
        isFamilyPep: yup.boolean().nullable(),
        isPepRelatedPerson: yup.boolean().nullable(),
        pepInfo: yup
          .object()
          .when('isPep', {
            is: true,
            then: (schema) => clientPep(schema).nullable(),
            otherwise: (schema) => schema.notRequired().nullable(),
          })
          .nullable(),

        familyPepsInfo: yup
          .array()
          .nullable()
          .when('isFamilyPep', {
            is: true,
            then: (schema) => schema.of(relatedPersonPep),
            otherwise: (schema) => schema.notRequired().nullable(),
          })
          .nullable(),

        pepRelatedPersonsInfo: yup
          .array()
          .nullable()
          .when('isPepRelatedPerson', {
            is: true,
            then: (schema) => schema.of(relatedPersonPep),
            otherwise: (schema) => schema.notRequired().nullable(),
          })
          .nullable(),

        hasForeignTaxResidency:
          isCRSIFopEnabled && isSoleTrader
            ? Yup.boolean().required(errorMessages.YES_NO_REQUIRED)
            : Yup.boolean().notRequired().nullable(),
        appeals: Yup.mixed().when('hasForeignTaxResidency', {
          is: true,
          then: () =>
            Yup.object()
              .shape({
                value: Yup.string().nullable(),
                label: Yup.string().required(errorMessages.FIELD_REQUIRED),
              })
              .nullable()
              .required(errorMessages.FIELD_REQUIRED),
          otherwise: () => Yup.mixed().nullable().notRequired(),
        }),
        jurisdictions: Yup.mixed().when('hasForeignTaxResidency', {
          is: true,
          then: () => jurisdictionsSchema,
          otherwise: () => Yup.mixed().nullable().notRequired(),
        }),
        accounts: Yup.mixed().nullable().notRequired(),
      })
      .test('at-least-one-selected', errorMessages.AT_LEAST_ONE_SELECTED, function (values) {
        const { isPep, isPepRelatedPerson, isPepsAbsent, isFamilyPep } = values;
        const filled = [isPep, isPepRelatedPerson, isPepsAbsent, isFamilyPep].filter(Boolean);
        if (filled.length) return true;
        return this.createError({
          path: `${this.path}.isPepsAbsent`,
          message: errorMessages.AT_LEAST_ONE_SELECTED,
        });
      })
  );

const fatcaTinRules = {
  [FATCA_TIN.SSN]: {
    regex: regularExpressions.FATCA_TIN_SSN,
    message: errorMessages.FATCA_TIN_SSN_FORMAT,
  },
  [FATCA_TIN.ITIN]: {
    regex: regularExpressions.FATCA_TIN_ITIN,
    message: errorMessages.FATCA_TIN_ITIN_FORMAT,
  },
  [FATCA_TIN.TIN]: {
    regex: regularExpressions.FATCA_TIN_TIN,
    message: errorMessages.FATCA_TIN_TIN_FORMAT,
  },
};

export const fatcaValidationSchema = (
  isSoleTrader: boolean,
  isFATCAFopEnabled: boolean,
  isUSA: boolean,
  isNativeCountryUSA: boolean,
  birthplaceCountryUSA: boolean,
  permanentAddressUSA: boolean
) =>
  yup.object({
    isForeignFinancialUnderFATCA:
      isFATCAFopEnabled && isSoleTrader
        ? yup.boolean().required(errorMessages.YES_NO_REQUIRED)
        : yup.boolean().notRequired().nullable(),
    fullNameInEng: yup
      .string()
      .nullable()
      .when('isForeignFinancialUnderFATCA', function ([isForeignFinancialUnderFATCA], schema) {
        return isForeignFinancialUnderFATCA
          ? Yup.string()
              .required(errorMessages.FIELD_REQUIRED)
              .matches(regularExpressions.LATIN_NAME_UPPERCASE, {
                message: errorMessages.LATIN_CHARACTERS_ONLY,
                excludeEmptyString: true,
              })
          : schema.notRequired();
      }),
    tinType: yup
      .string()
      .nullable()
      .when('isForeignFinancialUnderFATCA', function ([isForeignFinancialUnderFATCA], schema) {
        return isForeignFinancialUnderFATCA
          ? Yup.string().required(errorMessages.RADIO_OPTION_REQUIRED)
          : schema.notRequired();
      }),
    tin: yup
      .string()
      .nullable()
      .when(
        ['tinType', 'isForeignFinancialUnderFATCA'],
        ([tinType, isForeignFinancialUnderFATCA], schema) => {
          const rule = fatcaTinRules[tinType as keyof typeof fatcaTinRules];
          return rule && isForeignFinancialUnderFATCA
            ? schema
                .required(errorMessages.FIELD_REQUIRED)
                .matches(rule.regex, { message: rule.message, excludeEmptyString: true })
            : schema.notRequired();
        }
      ),
    refutationDocType: yup
      .string()
      .nullable()
      .when('isForeignFinancialUnderFATCA', function ([isForeignFinancialUnderFATCA], schema) {
        return isFATCAFopEnabled && isForeignFinancialUnderFATCA === false && isUSA
          ? schema.required(errorMessages.RADIO_OPTION_REQUIRED)
          : schema.notRequired();
      }),
    explanation: yup
      .object({
        usBirthAddress: yup.string().nullable(),
        message: yup.string().nullable(),
      })
      .nullable()
      .when(
        ['refutationDocType', 'isForeignFinancialUnderFATCA'],
        ([refutationDocType, isForeignFinancialUnderFATCA], schema) => {
          const isExplanationRequired =
            refutationDocType === REFUTATION_DOC_TYPE.EXPLANATION &&
            isNativeCountryUSA &&
            isForeignFinancialUnderFATCA === false;

          if (!isExplanationRequired) return schema.notRequired();

          const requiredText = yup
            .string()
            .required(errorMessages.FIELD_REQUIRED)
            .max(1000, errorMessages.MAX_LENGTH(1000));

          const optionalText = yup.string().nullable().notRequired();

          return schema.shape({
            usBirthAddress: birthplaceCountryUSA ? requiredText : optionalText,
            message: birthplaceCountryUSA || permanentAddressUSA ? requiredText : optionalText,
          });
        }
      ),
  });
